import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../../components/PageShell';
import { useHubExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { useP } from '../../i18n/useP';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { listBillingActuals } from '../../api/salesBillingActuals';
import { listOffers } from '../../api/salesOffers';
import { resolveTabs } from '../../i18n/resolveTabs';
import BillingActualTab from './BillingActualTab';
import Billing12MTab from './Billing12MTab';
import BillingSeasonalityTab from './BillingSeasonalityTab';
import './Sales.css';

/* RÉALISÉ (ex-« Facturation », 2026-10-20) : la section du réalisé des ventes. La rangée des commandes
   de la page pose d'abord la DONNÉE (Chiffre d'affaires | Facturation), puis la VUE (Réalisé · 12 mois
   glissants · Saisonnalité) : une seule carte, la donnée choisie valant pour les trois vues. Les deux
   natures sont deux grilles mensuelles qui ne se mélangent pas ; le CA se rattache à une offre et, en
   option, à une ligne de planification, et les Prévisions le lisent.

   FACTURATION (refaite le 2026-10-10 au format des autres pages de Ventes &
   Ops, proto `WIP/protos/facturation-homogene.html`) : la VUE se choisit sous
   le titre (Réalisé · 12 mois glissants · Saisonnalité), comme l'étape des
   Prévisions ; plus de ligne d'onglets. Les CATÉGORIES se filtrent en
   pastilles dans la barre d'outils de la carte, et le filtre vaut pour les
   trois vues : aucune pastille retenue, tout se lit. */
const TAB_DEFS = [
  { id: 'actual', labelKey: 'scenarii:billing.tabs.actual' },
  { id: 'rolling12m', labelKey: 'scenarii:billing.tabs.rolling12m' },
  { id: 'seasonality', labelKey: 'scenarii:billing.tabs.seasonality' },
];

export default function SalesBillingAnalysisPage({ activeTenant, addToast, year }) {
  const { t } = useTranslation();
  const p = useP();
  const TABS = resolveTabs(TAB_DEFS, t);
  const [tab, setTab] = useState('actual');
  const [donnee, setDonnee] = usePersistentValue(`sb_donnee_${activeTenant?.id || 'none'}`, 'ca');   // ca · billing
  const [saisie, setSaisie] = usePersistentValue(`sb_saisie_${activeTenant?.id || 'none'}`, 'mensuel');   // mensuel · cumul
  const [loading, setLoading] = useState(false);
  const [billingData, setBillingData] = useState(null);
  const [billingPrevYear, setBillingPrevYear] = useState(null);
  const [billingPrev2Year, setBillingPrev2Year] = useState(null);
  const [offers, setOffers] = useState([]);
  const [recherche, setRecherche] = useState('');            // le texte tapé : il filtre en direct, sans Entrée
  const [unit, setUnit] = useState('kchf');
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);

  async function loadBillingData(preserve = false) {
    if (!activeTenant) return;
    if (!preserve) setLoading(true);
    try {
      const [cur, prev, prev2] = await Promise.all([
        listBillingActuals(activeTenant.id, year, donnee),
        listBillingActuals(activeTenant.id, year - 1, donnee),
        listBillingActuals(activeTenant.id, year - 2, donnee),
      ]);
      const rows = cur.data || [];
      setBillingPrevYear(prev.data || []);
      setBillingPrev2Year(prev2.data || []);
      setBillingData(rows);
    } catch (err) {
      addToast(t('scenarii:billing.errors.load'), 'error');
    } finally {
      if (!preserve) setLoading(false);
    }
  }
  useEffect(() => { loadBillingData(false); }, [activeTenant?.id, year, donnee]); // eslint-disable-line
  useEffect(() => {
    if (!activeTenant?.id) return;
    listOffers(activeTenant.id).then((r) => setOffers(r.error ? [] : (r.data || []))).catch(() => setOffers([]));
  }, [activeTenant?.id]);

  /* LE FILTRE est un champ de recherche dans l'entête de la carte (demande d'Olivier, 2026-10-22) : on tape, les lignes dont
     le libellé contient le texte restent, sans accent ni casse, en direct. `selCats` = les identifiants retenus, ou `null`
     quand rien n'est tapé (tout s'affiche). */
  const normaliser = (x) => String(x || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const selCats = useMemo(() => {
    const q = normaliser(recherche.trim());
    return q ? new Set((billingData || []).filter((r) => normaliser(r.label).includes(q)).map((r) => r.id)) : null;
  }, [recherche, billingData]);
  const filteredData = useMemo(() => (billingData || []).filter((r) => !selCats || selCats.has(r.id)), [billingData, selCats]);
  const labels = useMemo(() => (selCats ? new Set(filteredData.map((r) => r.label)) : null), [selCats, filteredData]);
  const filtreEntete = (billingData || []).length > 0 ? (
    <input type="search" className="sb-filtre" value={recherche} onChange={(e) => setRecherche(e.target.value)}
           placeholder={donnee === 'ca' ? p('Rechercher une ligne…') : p('Rechercher une catégorie…')}
           aria-label={donnee === 'ca' ? p('Rechercher une ligne') : p('Rechercher une catégorie')} />
  ) : null;

  /* La vue de PAGE (étage 3) : elle commande la carte entière. */
  const vues = useMemo(() => [
    { id: 'donnee', kind: 'segmented', label: p('Données'), value: donnee, onChange: setDonnee,
      options: [{ id: 'ca', label: p("Chiffre d'affaires") }, { id: 'billing', label: p('Facturation') }] },
    { id: 'vue', kind: 'segmented', label: p('Vue'), value: tab, onChange: setTab,
      options: TABS.map((tb) => ({ id: tb.id, label: tb.label })) },
  ], [p, tab, t, donnee, setDonnee]); // eslint-disable-line

  // Rendu d'une vue — à l'écran (unité de l'écran) et pour l'export Excel
  // (CHF forcé : le classeur porte des francs, pas des milliers).
  const renderTab = (id, forExport = false) => {
    const u = forExport ? 'chf' : unit;
    const commun = { filtreEntete: forExport ? null : filtreEntete, unit: u };
    switch (id) {
      case 'actual':
        return (
          <>
            <BillingActualTab kind={donnee} saisie={saisie} onSaisie={setSaisie} tenantId={activeTenant?.id} year={year} selCats={selCats}
                              onDataChanged={() => loadBillingData(true)} addToast={addToast} {...commun} />
          </>
        );
      case 'rolling12m':
        return (
          <Billing12MTab dataYear={filteredData} dataPrevYear={billingPrevYear} dataPrev2Year={billingPrev2Year}
                         year={year} offers={offers} kind={donnee} {...commun} />
        );
      case 'seasonality':
        return (
          <BillingSeasonalityTab dataPrevYear={billingPrevYear} dataPrev2Year={billingPrev2Year}
                                 year={year} labels={labels} kind={donnee} {...commun} />
        );
      default: return null;
    }
  };

  // Export Excel — les trois vues d'un coup (une vue = une feuille).
  const excel = useHubExcelExport({
    tabs: TABS.map((tb) => ({ label: tb.label, render: () => renderTab(tb.id, true) })),
    fileBase: p('Revenus réalisés'),
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  const exportOptions = useMemo(() => [{ id: 'excel', label: 'Excel', run: excel.run }], [excel.run]);
  useRegisterPageExports(exportOptions);

  return (
    <PageShell framed kind="report" unit={pageUnit} title={p('Revenus réalisés')} views={vues}>
      {loading && <p className="sales-loading">{t('scenarii:billing.loading')}</p>}
      {!loading && renderTab(tab)}
      {excel.stage}
    </PageShell>
  );
}
